/**
 * Camera Calculations Utility
 *
 * مكتبة شاملة لحساب جميع القيم المتعلقة بأجهزة الكاميرا:
 * - حساب وقت التسجيل والمساحة المستخدمة
 * - تحليل أحداث كشف الحركة
 * - إحصائيات جودة البث والتسجيل
 * - إدارة التخزين والمساحة المتاحة
 * - تتبع الأحداث والتنبيهات
 *
 * @version 2.4.0
 * @since January 26, 2026
 */

// ═══════════════════════════════════════════════════════════
// Type Definitions - تعريفات الأنواع
// ═══════════════════════════════════════════════════════════

/**
 * Camera stream status
 */
export type StreamStatus = 'live' | 'recording' | 'idle' | 'offline';

/**
 * Video quality/resolution
 */
export type VideoQuality = '4K' | '1080p' | '720p' | '480p' | '360p';

/**
 * Camera event type
 */
export type CameraEventType =
  | 'motion_detected'
  | 'recording_started'
  | 'recording_stopped'
  | 'snapshot_taken'
  | 'alert_triggered';

/**
 * Camera event - single camera event
 */
export interface CameraEvent {
  timestamp: Date; // Event time
  type: CameraEventType; // Event type
  duration?: number; // Duration in seconds (for recordings)
  fileSize?: number; // File size in bytes (for recordings/snapshots)
  snapshotUrl?: string; // URL to snapshot/thumbnail
  resolution?: VideoQuality; // Recording resolution
  motionConfidence?: number; // Motion detection confidence (0-1)
  triggeredBy?: string; // What triggered the event
}

/**
 * Hourly camera activity pattern
 */
export interface CameraHourPattern {
  hour: number; // Hour (0-23)
  recordingCount: number; // Number of recordings
  motionCount: number; // Motion detection events
  totalRecordingSeconds: number; // Total recording time
  totalStorageBytes: number; // Total storage used
  isPeakHour: boolean; // Is this a peak activity hour?
}

/**
 * Camera timeline item for display
 */
export interface CameraTimelineItem {
  timestamp: Date;
  formattedTime: string; // "2:30 PM" or "14:30"
  type: CameraEventType;
  typeLabel: string; // "Motion Detected" or "تم كشف حركة"
  duration?: number;
  formattedDuration?: string; // "2m 5s" or "٢ د ٥ ث"
  fileSize?: number;
  formattedFileSize?: string; // "15.3 MB" or "١٥٫٣ م.ب"
  snapshotUrl?: string;
  resolution?: VideoQuality;
}

/**
 * Camera statistics
 */
export interface CameraStats {
  totalRecordings: number; // Total recordings count
  totalRecordingSeconds: number; // Total recording time
  totalStorageBytes: number; // Total storage used
  averageRecordingDuration: number; // Average recording length (seconds)
  longestRecording: number; // Longest recording (seconds)
  shortestRecording: number; // Shortest recording (seconds)
  motionDetectionCount: number; // Motion events detected
  snapshotCount: number; // Snapshots taken
  averageFileSize: number; // Average file size (bytes)
  peakHour: number; // Most active hour (0-23)
  quietestHour: number; // Least active hour (0-23)
  recordingQualityDistribution: {
    // Resolution distribution
    '4K': number;
    '1080p': number;
    '720p': number;
    '480p': number;
    '360p': number;
  };
}

/**
 * Camera sensor status
 */
export interface CameraStatus {
  streamStatus: StreamStatus; // Current stream status
  statusLabel: string; // "Live" or "مباشر"
  isRecording: boolean; // Currently recording?
  currentRecordingDuration: number; // Current recording time (seconds)
  resolution: VideoQuality; // Current resolution
  fps: number; // Frames per second
  bitrate: number; // Bitrate (kbps)
  storageUsedBytes: number; // Storage used (bytes)
  storageAvailableBytes: number; // Storage available (bytes)
  storageUsedPercentage: number; // Storage used %
  recordingCountToday: number; // Recordings today
  recordingCountWeek: number; // Recordings this week
  recordingCountMonth: number; // Recordings this month
  motionDetectedToday: number; // Motion events today
  lastRecording: Date | null; // Last recording time
  lastMotionDetected: Date | null; // Last motion time
  batteryLevel?: number; // Battery % (optional)
  signalStrength?: number; // Signal strength (optional)
  temperature?: number; // Camera temperature (optional)
  nightVisionEnabled?: boolean; // Night vision status
}

// ═══════════════════════════════════════════════════════════
// Core Calculation Functions - دوال الحسابات الأساسية
// ═══════════════════════════════════════════════════════════

/**
 * Calculate total recording time within date range
 *
 * @param events - Array of camera events
 * @param startDate - Start of counting period
 * @param endDate - End of counting period
 * @returns Total recording time in seconds
 *
 * @example
 * const recordingTime = calculateRecordingTime(events, todayStart, now);
 */
export function calculateRecordingTime(
  events: CameraEvent[],
  startDate: Date,
  endDate: Date,
): number {
  if (!events || events.length === 0) return 0;

  return events
    .filter(
      (event) =>
        (event.type === 'recording_started' || event.type === 'recording_stopped') &&
        event.timestamp >= startDate &&
        event.timestamp <= endDate &&
        event.duration !== undefined,
    )
    .reduce((total, event) => total + (event.duration || 0), 0);
}

/**
 * Calculate total storage usage
 *
 * @param events - Array of camera events
 * @param startDate - Start of analysis period
 * @param endDate - End of analysis period
 * @returns Total storage used in bytes
 *
 * @example
 * const storageUsed = getStorageUsage(events, todayStart, now);
 */
export function getStorageUsage(events: CameraEvent[], startDate: Date, endDate: Date): number {
  if (!events || events.length === 0) return 0;

  return events
    .filter(
      (event) =>
        event.timestamp >= startDate && event.timestamp <= endDate && event.fileSize !== undefined,
    )
    .reduce((total, event) => total + (event.fileSize || 0), 0);
}

/**
 * Count motion detection events
 *
 * @param events - Array of camera events
 * @param startDate - Start of counting period
 * @param endDate - End of counting period
 * @returns Number of motion detection events
 */
export function countMotionEvents(events: CameraEvent[], startDate: Date, endDate: Date): number {
  if (!events || events.length === 0) return 0;

  return events.filter(
    (event) =>
      event.type === 'motion_detected' &&
      event.timestamp >= startDate &&
      event.timestamp <= endDate,
  ).length;
}

/**
 * Get 24-hour camera activity pattern
 *
 * @param events - Array of camera events
 * @param date - Date to analyze (defaults to today)
 * @returns Array of 24 hourly patterns
 *
 * @example
 * const todayPattern = getCameraPattern(events);
 * const peakHours = todayPattern.filter(h => h.isPeakHour);
 */
export function getCameraPattern(events: CameraEvent[], date?: Date): CameraHourPattern[] {
  const targetDate = date || new Date();
  const dayStart = new Date(targetDate);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(dayStart);
  dayEnd.setHours(23, 59, 59, 999);

  // Initialize 24-hour array
  const hourlyData: CameraHourPattern[] = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    recordingCount: 0,
    motionCount: 0,
    totalRecordingSeconds: 0,
    totalStorageBytes: 0,
    isPeakHour: false,
  }));

  if (!events || events.length === 0) return hourlyData;

  // Filter events for the target day
  const dayEvents = events.filter((e) => e.timestamp >= dayStart && e.timestamp <= dayEnd);

  // Count events per hour
  dayEvents.forEach((event) => {
    const hour = event.timestamp.getHours();

    if (event.type === 'recording_started' || event.type === 'recording_stopped') {
      hourlyData[hour].recordingCount++;
      if (event.duration) {
        hourlyData[hour].totalRecordingSeconds += event.duration;
      }
      if (event.fileSize) {
        hourlyData[hour].totalStorageBytes += event.fileSize;
      }
    } else if (event.type === 'motion_detected') {
      hourlyData[hour].motionCount++;
    }
  });

  // Identify peak hours (above average + 1 std deviation)
  const activityCounts = hourlyData.map((h) => h.recordingCount + h.motionCount);
  const avgActivity = activityCounts.reduce((sum, count) => sum + count, 0) / 24;
  const variance =
    activityCounts.reduce((sum, count) => sum + Math.pow(count - avgActivity, 2), 0) / 24;
  const stdDev = Math.sqrt(variance);
  const peakThreshold = avgActivity + stdDev;

  hourlyData.forEach((hour) => {
    const activity = hour.recordingCount + hour.motionCount;
    hour.isPeakHour = activity > peakThreshold;
  });

  return hourlyData;
}

/**
 * Get camera activity timeline
 *
 * @param events - Array of camera events
 * @param maxItems - Maximum items to return (default: 20)
 * @returns Array of timeline items (most recent first)
 *
 * @example
 * const recentActivity = getCameraTimeline(events, 10);
 */
export function getCameraTimeline(
  events: CameraEvent[],
  maxItems: number = 20,
): CameraTimelineItem[] {
  if (!events || events.length === 0) return [];

  // Sort by most recent first
  const sortedEvents = [...events].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  const timeline: CameraTimelineItem[] = [];

  sortedEvents.slice(0, maxItems).forEach((event) => {
    const item: CameraTimelineItem = {
      timestamp: event.timestamp,
      formattedTime: event.timestamp.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      }),
      type: event.type,
      typeLabel: formatEventType(event.type, 'en'),
      resolution: event.resolution,
      snapshotUrl: event.snapshotUrl,
    };

    if (event.duration) {
      item.duration = event.duration;
      item.formattedDuration = formatRecordingTime(event.duration, 'en');
    }

    if (event.fileSize) {
      item.fileSize = event.fileSize;
      item.formattedFileSize = formatStorageSize(event.fileSize, 'en');
    }

    timeline.push(item);
  });

  return timeline;
}

/**
 * Calculate comprehensive camera statistics
 *
 * @param events - Array of camera events
 * @param startDate - Start of analysis period
 * @param endDate - End of analysis period
 * @returns Complete statistics object
 *
 * @example
 * const stats = getCameraStatistics(events, startOfWeek, endOfWeek);
 */
export function getCameraStatistics(
  events: CameraEvent[],
  startDate: Date,
  endDate: Date,
): CameraStats {
  if (!events || events.length === 0) {
    return {
      totalRecordings: 0,
      totalRecordingSeconds: 0,
      totalStorageBytes: 0,
      averageRecordingDuration: 0,
      longestRecording: 0,
      shortestRecording: 0,
      motionDetectionCount: 0,
      snapshotCount: 0,
      averageFileSize: 0,
      peakHour: 0,
      quietestHour: 0,
      recordingQualityDistribution: {
        '4K': 0,
        '1080p': 0,
        '720p': 0,
        '480p': 0,
        '360p': 0,
      },
    };
  }

  // Filter events in range
  const rangeEvents = events.filter((e) => e.timestamp >= startDate && e.timestamp <= endDate);

  // Count different event types
  const recordings = rangeEvents.filter(
    (e) => e.type === 'recording_started' || e.type === 'recording_stopped',
  );
  const motionEvents = rangeEvents.filter((e) => e.type === 'motion_detected');
  const snapshots = rangeEvents.filter((e) => e.type === 'snapshot_taken');

  // Calculate totals
  const totalRecordings = recordings.length;
  const totalRecordingSeconds = recordings.reduce((sum, e) => sum + (e.duration || 0), 0);
  const totalStorageBytes = rangeEvents.reduce((sum, e) => sum + (e.fileSize || 0), 0);

  // Calculate averages
  const recordingDurations = recordings
    .filter((e) => e.duration !== undefined)
    .map((e) => e.duration!);

  const averageRecordingDuration =
    recordingDurations.length > 0
      ? recordingDurations.reduce((sum, d) => sum + d, 0) / recordingDurations.length
      : 0;

  const longestRecording = recordingDurations.length > 0 ? Math.max(...recordingDurations) : 0;

  const shortestRecording = recordingDurations.length > 0 ? Math.min(...recordingDurations) : 0;

  const fileSizes = rangeEvents.filter((e) => e.fileSize !== undefined).map((e) => e.fileSize!);

  const averageFileSize =
    fileSizes.length > 0 ? fileSizes.reduce((sum, s) => sum + s, 0) / fileSizes.length : 0;

  // Find peak and quietest hours
  const pattern = getCameraPattern(rangeEvents);
  const peakHourData = pattern.reduce((prev, curr) =>
    curr.recordingCount + curr.motionCount > prev.recordingCount + prev.motionCount ? curr : prev,
  );
  const quietestHourData = pattern.reduce((prev, curr) =>
    curr.recordingCount + curr.motionCount < prev.recordingCount + prev.motionCount ? curr : prev,
  );

  // Calculate quality distribution
  const recordingQualityDistribution = {
    '4K': 0,
    '1080p': 0,
    '720p': 0,
    '480p': 0,
    '360p': 0,
  };

  recordings.forEach((event) => {
    if (event.resolution) {
      recordingQualityDistribution[event.resolution]++;
    }
  });

  return {
    totalRecordings,
    totalRecordingSeconds: Math.floor(totalRecordingSeconds),
    totalStorageBytes: Math.floor(totalStorageBytes),
    averageRecordingDuration: Math.floor(averageRecordingDuration),
    longestRecording: Math.floor(longestRecording),
    shortestRecording: Math.floor(shortestRecording),
    motionDetectionCount: motionEvents.length,
    snapshotCount: snapshots.length,
    averageFileSize: Math.floor(averageFileSize),
    peakHour: peakHourData.hour,
    quietestHour: quietestHourData.hour,
    recordingQualityDistribution,
  };
}

/**
 * Get current camera status
 *
 * @param events - Array of all camera events
 * @param streamStatus - Current stream status
 * @param resolution - Current resolution
 * @param fps - Current FPS
 * @param bitrate - Current bitrate (kbps)
 * @param storageUsed - Storage used (bytes)
 * @param storageTotal - Total storage capacity (bytes)
 * @param batteryLevel - Battery level (optional)
 * @param signalStrength - Signal strength (optional)
 * @param temperature - Camera temperature (optional)
 * @param nightVisionEnabled - Night vision status (optional)
 * @returns Complete camera status
 *
 * @example
 * const status = getCameraStatus(events, 'live', '1080p', 30, 2500, usedBytes, totalBytes);
 */
export function getCameraStatus(
  events: CameraEvent[],
  streamStatus: StreamStatus = 'idle',
  resolution: VideoQuality = '1080p',
  fps: number = 30,
  bitrate: number = 2500,
  storageUsed: number = 0,
  storageTotal: number = 100 * 1024 * 1024 * 1024, // 100GB default
  batteryLevel?: number,
  signalStrength?: number,
  temperature?: number,
  nightVisionEnabled?: boolean,
): CameraStatus {
  const now = new Date();
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);

  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - 7);

  const monthStart = new Date(now);
  monthStart.setMonth(now.getMonth() - 1);

  // Find last recording and motion
  const sortedEvents = [...events].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  const lastRecording =
    sortedEvents.find((e) => e.type === 'recording_started' || e.type === 'recording_stopped')
      ?.timestamp || null;

  const lastMotionDetected =
    sortedEvents.find((e) => e.type === 'motion_detected')?.timestamp || null;

  // Check if currently recording
  const isRecording = streamStatus === 'recording';

  // Calculate current recording duration if recording
  let currentRecordingDuration = 0;
  if (isRecording && lastRecording) {
    currentRecordingDuration = Math.floor((now.getTime() - lastRecording.getTime()) / 1000);
  }

  // Count recordings per period
  const recordingCountToday = events.filter(
    (e) =>
      (e.type === 'recording_started' || e.type === 'recording_stopped') &&
      e.timestamp >= todayStart &&
      e.timestamp <= now,
  ).length;

  const recordingCountWeek = events.filter(
    (e) =>
      (e.type === 'recording_started' || e.type === 'recording_stopped') &&
      e.timestamp >= weekStart &&
      e.timestamp <= now,
  ).length;

  const recordingCountMonth = events.filter(
    (e) =>
      (e.type === 'recording_started' || e.type === 'recording_stopped') &&
      e.timestamp >= monthStart &&
      e.timestamp <= now,
  ).length;

  // Count motion events today
  const motionDetectedToday = countMotionEvents(events, todayStart, now);

  // Calculate storage usage
  const storageAvailableBytes = Math.max(0, storageTotal - storageUsed);
  const storageUsedPercentage =
    storageTotal > 0 ? Math.round((storageUsed / storageTotal) * 100) : 0;

  // Get status label
  const statusLabel = formatStreamStatus(streamStatus, 'en');

  return {
    streamStatus,
    statusLabel,
    isRecording,
    currentRecordingDuration,
    resolution,
    fps,
    bitrate,
    storageUsedBytes: storageUsed,
    storageAvailableBytes,
    storageUsedPercentage,
    recordingCountToday,
    recordingCountWeek,
    recordingCountMonth,
    motionDetectedToday,
    lastRecording,
    lastMotionDetected,
    batteryLevel,
    signalStrength,
    temperature,
    nightVisionEnabled,
  };
}

// ═══════════════════════════════════════════════════════════
// Formatting Functions - دوال التنسيق
// ═══════════════════════════════════════════════════════════

/**
 * Format recording time for display
 *
 * @param seconds - Duration in seconds
 * @param language - Display language ('ar' or 'en')
 * @returns Formatted time string
 *
 * @example
 * formatRecordingTime(125, 'en') // "2m 5s"
 * formatRecordingTime(125, 'ar') // "٢ د ٥ ث"
 */
export function formatRecordingTime(seconds: number, language: 'ar' | 'en' = 'en'): string {
  if (seconds < 60) {
    return language === 'ar' ? `${seconds.toLocaleString('ar-SA')} ث` : `${seconds}s`;
  }

  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;

    if (language === 'ar') {
      return secs > 0
        ? `${minutes.toLocaleString('ar-SA')} د ${secs.toLocaleString('ar-SA')} ث`
        : `${minutes.toLocaleString('ar-SA')} د`;
    }

    return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`;
  }

  // Hours
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (language === 'ar') {
    return minutes > 0
      ? `${hours.toLocaleString('ar-SA')} س ${minutes.toLocaleString('ar-SA')} د`
      : `${hours.toLocaleString('ar-SA')} س`;
  }

  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
}

/**
 * Format storage size for display
 *
 * @param bytes - Size in bytes
 * @param language - Display language ('ar' or 'en')
 * @returns Formatted size string
 *
 * @example
 * formatStorageSize(1536000, 'en') // "1.5 MB"
 * formatStorageSize(1536000, 'ar') // "١٫٥ م.ب"
 */
export function formatStorageSize(bytes: number, language: 'ar' | 'en' = 'en'): string {
  const units =
    language === 'ar' ? ['بايت', 'ك.ب', 'م.ب', 'ج.ب', 'ت.ب'] : ['B', 'KB', 'MB', 'GB', 'TB'];

  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  const formatted = size < 10 ? size.toFixed(1) : Math.round(size).toString();

  if (language === 'ar') {
    return `${parseFloat(formatted).toLocaleString('ar-SA')} ${units[unitIndex]}`;
  }

  return `${formatted} ${units[unitIndex]}`;
}

/**
 * Format video resolution for display
 *
 * @param resolution - Video quality
 * @param language - Display language ('ar' or 'en')
 * @returns Formatted resolution string
 */
export function formatResolution(resolution: VideoQuality, language: 'ar' | 'en' = 'en'): string {
  if (language === 'ar') {
    const resolutionMap: Record<VideoQuality, string> = {
      '4K': '٤ك (٣٨٤٠×٢١٦٠)',
      '1080p': '١٠٨٠ب (١٩٢٠×١٠٨٠)',
      '720p': '٧٢٠ب (١٢٨٠×٧٢٠)',
      '480p': '٤٨٠ب (٨٥٤×٤٨٠)',
      '360p': '٣٦٠ب (٦٤٠×٣٦٠)',
    };
    return resolutionMap[resolution];
  }

  return resolution;
}

/**
 * Format stream status for display
 *
 * @param status - Stream status
 * @param language - Display language ('ar' or 'en')
 * @returns Formatted status string
 */
export function formatStreamStatus(status: StreamStatus, language: 'ar' | 'en' = 'en'): string {
  if (language === 'ar') {
    const statusMap: Record<StreamStatus, string> = {
      live: 'مباشر',
      recording: 'يسجل',
      idle: 'خامل',
      offline: 'غير متصل',
    };
    return statusMap[status];
  }

  const statusMap: Record<StreamStatus, string> = {
    live: 'Live',
    recording: 'Recording',
    idle: 'Idle',
    offline: 'Offline',
  };
  return statusMap[status];
}

/**
 * Format event type for display
 *
 * @param type - Event type
 * @param language - Display language ('ar' or 'en')
 * @returns Formatted event type string
 */
export function formatEventType(type: CameraEventType, language: 'ar' | 'en' = 'en'): string {
  if (language === 'ar') {
    const typeMap: Record<CameraEventType, string> = {
      motion_detected: 'تم كشف حركة',
      recording_started: 'بدأ التسجيل',
      recording_stopped: 'توقف التسجيل',
      snapshot_taken: 'تم التقاط لقطة',
      alert_triggered: 'تم إطلاق تنبيه',
    };
    return typeMap[type];
  }

  const typeMap: Record<CameraEventType, string> = {
    motion_detected: 'Motion Detected',
    recording_started: 'Recording Started',
    recording_stopped: 'Recording Stopped',
    snapshot_taken: 'Snapshot Taken',
    alert_triggered: 'Alert Triggered',
  };
  return typeMap[type];
}

/**
 * Get stream quality description
 *
 * @param bitrate - Bitrate in kbps
 * @param language - Display language ('ar' or 'en')
 * @returns Quality description
 *
 * @example
 * getStreamQuality(5000, 'en') // "Excellent"
 * getStreamQuality(1000, 'ar') // "متوسط"
 */
export function getStreamQuality(bitrate: number, language: 'ar' | 'en' = 'en'): string {
  if (bitrate >= 4000) {
    return language === 'ar' ? 'ممتاز' : 'Excellent';
  }

  if (bitrate >= 2500) {
    return language === 'ar' ? 'جيد جداً' : 'Very Good';
  }

  if (bitrate >= 1500) {
    return language === 'ar' ? 'جيد' : 'Good';
  }

  if (bitrate >= 800) {
    return language === 'ar' ? 'متوسط' : 'Fair';
  }

  return language === 'ar' ? 'ضعيف' : 'Poor';
}

/**
 * Format FPS for display
 *
 * @param fps - Frames per second
 * @param language - Display language ('ar' or 'en')
 * @returns Formatted FPS string
 */
export function formatFPS(fps: number, language: 'ar' | 'en' = 'en'): string {
  if (language === 'ar') {
    return `${fps.toLocaleString('ar-SA')} إطار/ث`;
  }

  return `${fps} FPS`;
}

/**
 * Format bitrate for display
 *
 * @param kbps - Bitrate in kbps
 * @param language - Display language ('ar' or 'en')
 * @returns Formatted bitrate string
 */
export function formatBitrate(kbps: number, language: 'ar' | 'en' = 'en'): string {
  if (kbps >= 1000) {
    const mbps = kbps / 1000;
    const formatted = mbps < 10 ? mbps.toFixed(1) : Math.round(mbps).toString();

    if (language === 'ar') {
      return `${parseFloat(formatted).toLocaleString('ar-SA')} م.ب/ث`;
    }

    return `${formatted} Mbps`;
  }

  if (language === 'ar') {
    return `${kbps.toLocaleString('ar-SA')} ك.ب/ث`;
  }

  return `${kbps} Kbps`;
}
